import {Reservation} from '../../models/reservation';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {MatDialog} from '@angular/material';
import {environment} from '../../../environments/environment';
import {ActionQueue} from '@cicero/webui/lib/core/shared/action-queue';
import {CuiAlertDialogComponent} from '@cicero/webui/lib/core/dialogs/alert-dialog';
import {HttpClient} from '@angular/common/http';
import {AuthenticationService} from '../../authentication/shared/authentication.service';
import {isEqual, uniqWith} from 'lodash';

@Injectable()
export class ReservationService {
  private notFoundQueue = new ActionQueue<Reservation>();
  private foundQueue = new ActionQueue<Reservation>();

  constructor(private dialog: MatDialog, private http: HttpClient, private auth: AuthenticationService) {
    window.addEventListener('online', () => this.syncActions());
  }

  markNotFound(reservation: Reservation): Observable<string> {
    reservation.state = 'not found';
    reservation.canUndo = true;

    if (!window.navigator.onLine) {
      this.notFoundQueue.mark(reservation.reservationId, reservation);
      return Observable.empty();
    } else {
      return this.sendMarkNotFoundRequest(reservation);
    }
  }

  markFound(reservation: Reservation): Observable<string> {
    reservation.state = 'found';
    reservation.canUndo = true;

    if (!window.navigator.onLine) {
      this.foundQueue.mark(reservation.reservationId, reservation);
      return Observable.empty();
    } else {
      return this.sendMarkFoundRequest(reservation);
    }
  }

  markSkipped(reservation: Reservation): void {
    reservation.state = 'skip';
    reservation.canUndo = true;
  }

  undo(reservation: Reservation): Observable<any> {
    if (!reservation.canUndo) {
      return Observable.throw('Undoing the latest action on this reservation is not allowed');
    }

    let request = Observable.empty();

    if (reservation.state === 'found') {
      if (!window.navigator.onLine) {
        this.foundQueue.undo(reservation.reservationId, reservation);
      } else {
        request = this.sendUndoFoundRequest(reservation);
      }
    } else if (reservation.state === 'not found') {
      if (!window.navigator.onLine) {
        this.notFoundQueue.undo(reservation.reservationId, reservation);
      } else {
        request = this.sendUndoNotFoundRequest(reservation);
      }
    }

    reservation.state = undefined;
    reservation.canUndo = false;

    return request;
  }

  private showMissingThemeDialog() {
    const ref = this.dialog.open(CuiAlertDialogComponent, {
      data: {
        title: 'Tema ikke sat op',
        content: `
          Materialerne kan ikke tildeles et tema.
          Kontakt din systemadministrator for at få et tema korrekt forbundet med Cicero Move.
        `.trim(),
        buttonLabel: 'Forstået'
      }
    });

    return ref.afterClosed().switchMap(() => Observable.empty());
  }

  private syncActions() {

    this.filterDuplicatedActions(this.foundQueue.getActions()).forEach((action: any) => {
      if (action.isUndo) {
        this.sendUndoFoundRequest(action.data).subscribe();
      } else {
        this.sendMarkFoundRequest(action.data).subscribe();
      }
    });

    this.filterDuplicatedActions(this.notFoundQueue.getActions()).forEach((action: any) => {
      if (action.isUndo) {
        this.sendUndoNotFoundRequest(action.data).subscribe();
      } else {
        this.sendMarkNotFoundRequest(action.data).subscribe();
      }
    });

    this.foundQueue.clear();
    this.notFoundQueue.clear();
  }

  private filterDuplicatedActions(actions: any[]) {
    return uniqWith(actions, (a, b) => isEqual(a.data.materials.map((m: any) => m.materialId), b.data.materials.map((m: any) => m.materialId)));
  }

  private sendMarkFoundRequest(reservation: Reservation): Observable<string> {
    return this.http.put(
      `${environment.apiUrl}/reservation/found`,
      reservation.reservationId,
      {headers: this.auth.getAuthHeaders(), responseType: 'text'}
    );
  }

  private sendMarkNotFoundRequest(reservation: Reservation): Observable<string> {
    const materialIds = reservation.materials
      .filter(m => m.placementInfo === reservation.placementInfo)
      .map(m => m.materialId);
    return this.http.put(
      `${environment.apiUrl}/reservation/notFound`,
      materialIds,
      {headers: this.auth.getAuthHeaders(), responseType: 'text'}
    )
      .catch(error => {
        reservation.state = undefined;
        reservation.canUndo = undefined;

        if (error.status === 412) {
          return this.showMissingThemeDialog();
        }

        return Observable.throw(error);
      });
  }

  private sendUndoFoundRequest(reservation: Reservation): Observable<string> {
    return this.http.put(
      `${environment.apiUrl}/reservation/found/undo`,
      reservation.reservationId,
      {headers: this.auth.getAuthHeaders(), responseType: 'text'}
    );
  }

  private sendUndoNotFoundRequest(reservation: Reservation): Observable<string> {
    const materialIds = reservation.materials
      .filter(m => m.placementInfo === reservation.placementInfo)
      .map(m => m.materialId);

    return this.http.put(
      `${environment.apiUrl}/reservation/notFound/undo`,
      materialIds,
      {headers: this.auth.getAuthHeaders(), responseType: 'text'}
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/pick-up-list/shared/reservation.service.ts